import {defineStore} from 'pinia';
import {TenantData} from "@/shared/models/objects.ts";
import {appService} from "@/shared/services/app.service.ts";
import {AppFeature, OrderedVariant, Tenant} from "@/shared/graphql/types";
import {appSettings} from "@@/settings.ts"

/**
 * The store loads only the data needed to display the basic application, tenant information, colorscheme information ,...
 * No authentication is needed to use this store, for faster reloads Tenant information is stored in localstorage.
 */
let lang = null as string;
export const useTenantStore = defineStore('tenantStore', {
    state: () => ({
        tenant: null as TenantData,
        mostOrderedVariants: null as OrderedVariant[] | null,
        loading: true,
        authenticatedDataLoaded: false,
    }),
    getters: {
        placeholder: (state) => state.tenant?.applicationSettings?.images?.logo?.responsive,
        placeholderImage: (state) => state.tenant?.applicationSettings?.images?.logo,
    },
    actions: {
        reset() {
        },
        hasFeature(name: string): boolean {
            if (name === "scanning") return this.tenant?.applicationSettings.featureSettings.scanSettings?.licenseKey != null
            return this.tenant?.applicationSettings?.app?.features?.some((f: AppFeature) => f.name == name) ?? false;
        },
        setDocumentTitle(text: string) {
            setDocumentTitle(this.tenant, text)
        },
        async init(language: string): Promise<TenantData> {
            const l = language.split("-")[0]
            if (lang != null && lang != l) { //Refresh the tenant information
                lang = l;
                this.tenant = await appService.loadTenantData(lang)
                return this.tenant
            }
            if (this.tenant == null && !this.tenantLoading) {
                lang = l;
                this.loading = true;
                const localTenantData = await appService.loadLocalStorageTenantData(lang)

                if (localTenantData) {
                    this.tenant = localTenantData
                    this.loading = false;
                    setDocumentTitle(this.tenant)
                    setDocumentFavIcons(this.tenant)
                    setMetaThemColor(this.tenant.applicationSettings.colors.primary)
                    //Load "fresh" tenant in the background
                    appService.loadTenantData(lang).then(async (td: TenantData) => {
                        this.tenant = td
                    })
                } else {
                    this.tenant = await appService.loadTenantData(lang)
                    this.loading = false;
                    setDocumentTitle(this.tenant)
                    setDocumentFavIcons(this.tenant)
                }
            }
            return this.tenant;
        },
        async initAuthenticated(language: string): Promise<Tenant> {
            if (language != lang) this.authenticatedDataLoaded = false
            if (this.authenticatedDataLoaded) return this.tenant;
            lang = language.split("-")[0];
            const tenant = await appService.loadAuthenticatedTenantData(lang)
            this.mostOrderedVariants = tenant.catalog.orderedVariants.results;
            this.authenticatedDataLoaded = true;
            return tenant
        }
    }
});

const setDocumentTitle = (tenant: TenantData, text?: String) => {
    document.title = `${tenant.tenantInfo.name} ${appSettings.title()}` + (text ? ` - ${text}` : "")
}
const setMetaThemColor = (primaryColor: string) => {
    let metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
        metaThemeColor.setAttribute('content', primaryColor);
    } else {
        metaThemeColor = document.createElement('meta');
        metaThemeColor.setAttribute('name', 'theme-color');
        metaThemeColor.setAttribute('content', primaryColor);
        document.head.appendChild(metaThemeColor);
    }
}
const setDocumentFavIcons = (tenant: TenantData) => {
    // Clear any existing favicons
    const existingFavIcons = document.querySelectorAll('link[rel="icon"]');
    existingFavIcons.forEach((icon) => icon.remove());

    // Add new favicons from the tenant settings
    const {favIcons} = tenant.applicationSettings;
    if (favIcons && favIcons.length > 0) {
        favIcons.forEach((favicon) => {
            const link = document.createElement('link');
            link.setAttribute('rel', 'icon');
            link.setAttribute('type', favicon.type);
            link.setAttribute('sizes', favicon.sizes);
            link.setAttribute('href', favicon.src);
            document.head.appendChild(link);
        });
    }
};